import React from 'react';
import { AlertCircle, RefreshCw, Table } from 'lucide-react';

/**
 * ErrorBoundary - Catches React rendering errors and provides graceful fallback
 *
 * Wraps components to prevent entire app crashes from propagating.
 * Displays user-friendly error message with retry option.
 *
 * @example
 * <ErrorBoundary title="Custom Error" message="Custom message">
 *   <UploadComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so next render shows fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught error:', error, errorInfo);
    this.setState({ errorInfo });

    // Optional: Send to error reporting service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    // Reset error state to try again
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI from props
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      const errorMessage = this.props.message || this.state.error?.message || 'An unexpected error occurred';
      const title = this.props.title || 'Something went wrong';
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className="bg-red-900/20 border border-red-900/50 text-red-200 p-6 rounded-lg">
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-2">{title}</h3>
              <p className="text-sm mb-4">{errorMessage}</p>

              {isDevelopment && this.state.errorInfo && (
                <details className="text-xs bg-red-950/50 p-3 rounded mb-4">
                  <summary className="cursor-pointer font-semibold mb-2">
                    Error Details (Development Only)
                  </summary>
                  <pre className="overflow-auto">
                    {this.state.error?.toString()}
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={this.handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>

            {this.props.onViewRawData && (
              <button
                onClick={this.props.onViewRawData}
                className="flex items-center gap-2 px-4 py-2 bg-red-800 hover:bg-red-900 text-white rounded-lg transition-colors"
              >
                <Table className="w-4 h-4" />
                View Raw Data
              </button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Chart-specific error boundary with optimized fallback UI
 *
 * @example
 * <ChartErrorBoundary chartName="Dispersion Chart">
 *   <DispersionChart {...props} />
 * </ChartErrorBoundary>
 */
export function ChartErrorBoundary({ children, chartName, onViewRawData }) {
  return (
    <ErrorBoundary
      title={`${chartName || 'Chart'} failed to load`}
      message="The chart encountered an error while rendering. You can try reloading or view the data in table format."
      onViewRawData={onViewRawData}
    >
      {children}
    </ErrorBoundary>
  );
}
